// Video settings > Autopilot: the same two sides as Product settings >
// Autopilot, for each product this video is on — the product's pages with
// their switches, and the Video card narrowed to this video.
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { DotLottleLoader } from '../../../../components/Loader';
import { usePortalPath } from '../../../../hooks/usePortalPath';
import { useUser } from '../../../../hooks/useUser';
import { isInternal } from '../../../../utils';
import { AutopilotSides } from '../../productsV2/productModal/AutopilotSides';
import { useProductAutopilot } from '../../productsV2/productModal/useProductAutopilot';
import { btnSecondary } from '../../productsV2/productModal/autopilotLabels';

interface ProductRef {
  desireid: number;
  title: string | null;
}

function ProductBlock({ product, brandId, videoId, showTitle }: { product: ProductRef; brandId: number | null; videoId: number; showTitle: boolean }) {
  const { role } = useUser();
  const { p } = usePortalPath();
  const { data, loading, error, reload } = useProductAutopilot(product.desireid, brandId);
  const productHref =
    isInternal(role) && brandId ? p(`/products/${brandId}?productId=${product.desireid}`) : p(`/products?productId=${product.desireid}`);
  return (
    <Box className="flex flex-col gap-2">
      {showTitle ? (
        <a href={productHref} target="_blank" rel="noreferrer" className="text-[13px] font-semibold text-text-1 hover:text-accent-text self-start">
          {product.title || `Product #${product.desireid}`}
        </a>
      ) : null}
      {loading ? <DotLottleLoader fill={false} /> : null}
      {!loading && error ? (
        <Box className="flex flex-col items-start gap-2">
          <p className="text-[13px] text-down m-0">{error}</p>
          <button type="button" className={btnSecondary} onClick={() => reload(false)}>
            Try again
          </button>
        </Box>
      ) : null}
      {!loading && !error && data ? (
        <AutopilotSides desireid={product.desireid} brandId={brandId} bundle={data} reload={reload} onlyVideoId={videoId} />
      ) : null}
      {!loading && !error && !data ? <p className="text-[12.5px] text-muted m-0">This product has no pages to run Autopilot on yet.</p> : null}
    </Box>
  );
}

export function VideoAutopilotPanel({ video, brandId }: { video: any; brandId: number | null }) {
  const products: ProductRef[] = useMemo(
    () =>
      ((video && video.videoProducts) || [])
        .filter((pr: any) => pr && pr.desireid)
        .map((pr: any) => ({ desireid: Number(pr.desireid), title: pr.title || null })),
    [video],
  );
  return (
    <Box className="h-full overflow-y-auto flex flex-col gap-6">
      {products.length ? (
        products.map((pr) => (
          <ProductBlock key={pr.desireid} product={pr} brandId={brandId} videoId={Number(video.id)} showTitle={products.length > 1} />
        ))
      ) : (
        <Box className="rounded-[12px] border border-dashed border-border-token bg-surface-2 p-5 text-center">
          <p className="text-[13.5px] font-semibold text-text-1 m-0">Put this video on a product to start</p>
          <p className="text-[12.5px] text-muted mt-1 mb-0">Autopilot runs per product: its pages, and this video on it.</p>
        </Box>
      )}
    </Box>
  );
}
